import * as _ from 'underscore';
import { Component, OnInit, HostBinding, ChangeDetectionStrategy, Input, Output, ChangeDetectorRef, EventEmitter } from '@angular/core';
import { AlertTypes, ConditionMonitoringService, IDisplayVehicle, IPositionData, ISelectedPosition, MonitoringIssueData } from '../../../condition-monitoring.service'
import { Observable } from 'rxjs';
import { XlatPipe } from '@amt/i18n/xlat-pipe';
import OcConfigSvc from '@Services/ocConfigSvc';
import { IOcVehicleSchematicConfig } from '@Interfaces/visualInspection.interfaces';
import { getInspectionDueDate } from '@OC-common/utils/condition-monitoring-visual-inspections.utils';

export const ICON_WIDTH = 196;
export const ICON_HEIGHT = 196;
const NON_OVERDUED_ACTIONS = [
    'VisualInspectionAction.Continue to Monitor',
    'VisualInspectionAction.Schedule for Removal in Next Service',
];

@Component({
    selector: 'condition-details',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './condition-details.component.html',
    styleUrls: ['./condition-details.component.scss'],
    providers: [
        { provide: 'desiredWidth', useValue: ICON_WIDTH },
        { provide: 'desiredHeight', useValue: ICON_HEIGHT },
        ConditionMonitoringService]
})
export class ConditionDetailsComponent implements OnInit {
    @Input() vehicle!: IDisplayVehicle;
    @Input() currentVehicle$!: Observable<any>;
    @Input() currentVehicle: any;
    isProcessing: boolean;
    selectedComponent: ISelectedPosition = null;
    ocVehicleSchematicConfig: IOcVehicleSchematicConfig = {
        maxWidth: 300,
        maxHeight: 350,
        leftPos: 1,
        topAxle: 1
    };
    @Output() updateSelectedComponent = new EventEmitter<ISelectedPosition>();
    @Output() finishedProcessing = new EventEmitter<boolean>();

    constructor(
        private monSvc: ConditionMonitoringService,
        private xlatPipe: XlatPipe,
        private ocConfigSvc: OcConfigSvc,
        private ref: ChangeDetectorRef,
    ) {
    }

    @HostBinding("style.--img-w") public readonly cssImgWidth = ICON_WIDTH;
    @HostBinding("style.--img-h") public readonly cssImgHeight = ICON_HEIGHT;

    ngOnInit(): void {
        //        this.isProcessing = true;
        if (!this.currentVehicle) {
            this.currentVehicle$.subscribe((currentVehicle: any) => {
                this.doneProcessing();
                this.currentVehicle = currentVehicle;
            });
        } else {
            this.doneProcessing()
        }
    }

    private doneProcessing() {
        this.finishedProcessing.emit(true);
    }

    //This is fired from ocVehicleSchematic when a tyre is selected
    tyreSelected(tyre: IPositionData) {
        if (tyre.component.type !== "Tyre" || (!tyre.component.equipmentId || tyre.component.equipmentId.length === 0))
            return; //Should throw an error?

        //If it is the currently selected tyre then unselect it? How does that work with the ocschematic...
        if (this.selectedComponent !== null && this.selectedComponent.equipmentId == tyre.component.equipmentId) {
            this.selectedComponent = null;
        } else {
            this.selectedComponent = tyre.component;

            this.selectedComponent.fittedVehicle = this.vehicle.serialNo;
            this.selectedComponent.percentWorn = ((1 - tyre.component.remainingDepth.value / tyre.component.primaryOTD.value) * 100).toFixed(this.ocConfigSvc.general.percentageWornDecimals).toString() + "%";
            this.selectedComponent.positionLabel = tyre.position;
        }

        this.updateSelectedComponent.emit(this.selectedComponent);

        this.ref.markForCheck();
    }

    //Public property so template can access alert types enum
    public get alertTypes(): typeof AlertTypes {
        return AlertTypes;
    }

    //Returns boolean if an alert of the specified type exists
    alertExists(alertName: string): boolean {
        if(this.selectedComponent === null) return this.currentVehicle.vehicleAlerts.some((u:MonitoringIssueData) => u.cause === alertName);
        return this.currentVehicle.vehicleAlerts.some((u:MonitoringIssueData) => u.cause === alertName && u.equipmentId === this.selectedComponent.equipmentId);
        /* return this.selectedComponent === null
            ? this.currentVehicle.vehicleAlerts.some(u => u.alertTypeName === alertName) : this.currentVehicle.vehicleAlerts.some(u => u.alertTypeName === alertName && u.equipmentId === this.selectedComponent.equipmentId); */
    }

    //Returns the status of the vehicles visual inspections
    viStatus(): string {
        const visualInspectionDetails = this.selectedComponent === null
            ? this.currentVehicle.visualInspectionDetails
            : this.currentVehicle.visualInspectionDetails.filter(obj => obj.equipmentId === this.selectedComponent.equipmentId);
        const items = visualInspectionDetails
            .flatMap(a => a.visualInspectionItems)
            .filter(obj => obj.active);

        if (items.length === 0) return 'conditionmonitoring.statusok';
        for(let i = 0; i < items.length; i+= 1) {
            const { visualInspectionRecord } = items[i];
            if (!NON_OVERDUED_ACTIONS.some(a => a === visualInspectionRecord.action) && getInspectionDueDate(visualInspectionRecord).getTime() < Date.now())
                return 'conditionmonitoring.vi_statusactionoverdue';
        }
        return 'conditionmonitoring.statusactionrequired';
    }

    highlightViStatus(viStatus: string) : boolean {
        return viStatus !== 'conditionmonitoring.statusok';
    }
    highAlertHighlighStatus(viStatus: string): boolean {
        return viStatus === 'conditionmonitoring.vi_statusactionoverdue';
    }

    //Returns a count of visual inspections. Optionally active only
    viCount(activeOnly: boolean) {
        return this.monSvc.viCount(activeOnly, this.selectedComponent, this.currentVehicle);
    }

    //Get the date from the latest VI
    latestViDate(): string {
        var items = this.monSvc.getItems(this.selectedComponent, this.currentVehicle);
        return items.length === 0 ? "-" : this.monSvc.formatDate(items[0].visualInspectionRecord.date);
    }

    //Gets the user from the latest VI
    latestViUser(): string {
        var items = this.monSvc.getItems(this.selectedComponent, this.currentVehicle);
        return items.length === 0 ? "-" : items[0].visualInspectionRecord.inspectedBy;
    }

    percentWorn(): string {
        return this.selectedComponent === null ? "" : `(${this.selectedComponent.percentWorn})`;
    }

    tableHeaderDetails(option?: string): string {
        if (typeof (option) !== 'undefined')
            return this.selectedComponent === null ? this.xlatPipe.transform(option) : `${this.xlatPipe.transform(option)} - Pos. ${this.selectedComponent.positionLabel}`;

        return this.selectedComponent === null ? this.xlatPipe.transform("conditionmonitoring.NoTyreDetails") : `${this.xlatPipe.transform("conditionmonitoring.TyreDetails")} - Pos. ${this.selectedComponent.positionLabel}`;
    }

    showVehicle() {
        this.monSvc.showVehicle(this.vehicle, this.currentVehicle);
    }

    showComponent() {
        this.monSvc.showComponent(this.selectedComponent);
    }
}

